import React, {FC, useState} from 'react';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {CardModel} from '../../../models/api/cardModel';
import {ContactCardPanelId} from '../../../store/panels/panelStateBase';
import {ContactCardDetailsCustomFieldsContainer} from './contactCardDetailsCustomFieldsContainer';
import {ContactCardViewMoreButton} from './contactCardViewMoreButton';
import {ContactCardChildItem} from './items/contactCardChildItem';

/*
 * Constants.
 */

const maxAccountItems = 4;

/*
 * Props.
 */

interface ContactCardAccountDetailsProps {
  panelId: ContactCardPanelId;
  account: AccountModel;
  card: CardModel;
}

/*
 * Component.
 */

export const ContactCardAccountDetails: FC<ContactCardAccountDetailsProps> = (props) => {
  const {account} = props;
  const [isShowingAdditionalFields, setIsShowingAdditionalFields] = useState(false);

  return (
    <>
      {maybeRenderEmailDomains(account)}
      {maybeRenderDescription(account)}
      <ContactCardDetailsCustomFieldsContainer
        account={account}
        customAttributes={account.customFieldAttributes ?? []}
        // Calculate the room based on the already rendered fields.
        availableItemsToRender={computeAvailableCustomFieldsToRender(account, isShowingAdditionalFields)}
      />
      {maybeRenderViewMoreFields(props, isShowingAdditionalFields, setIsShowingAdditionalFields)}
    </>
  );
};

/*
 * Helpers.
 */

function computeAvailableCustomFieldsToRender(account: AccountModel, isShowingAdditionalFields: boolean) {
  if (isShowingAdditionalFields) {
    return Number.MAX_SAFE_INTEGER;
  }
  return maxAccountItems - (account.domains.length > 0 ? 1 : 0) - (account.description ? 1 : 0);
}

function maybeRenderEmailDomains(account: AccountModel) {
  if (!account.domains.length) {
    return null;
  }
  const emailDomainsJoined = account.domains.join(', ');
  return (
    <ContactCardChildItem
      title={<Trans id="contact-card-account-details-email-domains-title">Email Domains</Trans>}
      text={emailDomainsJoined}
      shouldAllowMultiLineWrapping
    >
      {emailDomainsJoined}
    </ContactCardChildItem>
  );
}

function maybeRenderDescription(account: AccountModel) {
  if (!account.description) {
    return null;
  }
  return (
    <ContactCardChildItem
      text={account.description}
      title={<Trans id="contact-card-account-details-description-title">Description</Trans>}
      shouldAllowMultiLineWrapping
    >
      {account.description}
    </ContactCardChildItem>
  );
}

function maybeRenderViewMoreFields(
  props: ContactCardAccountDetailsProps,
  isShowingAdditionalFields: boolean,
  setIsShowingAdditionalFields: HandlerOf<boolean>,
) {
  if (isShowingAdditionalFields) {
    return null;
  }

  const {account} = props;
  // Add all the fields together.
  const totalAvailableItems =
    (account.domains.length ? 1 : 0) +
    (account.description ? 1 : 0) +
    (account.customFieldAttributes ?? []).length;
  const additionalItemsCount = Math.max(0, totalAvailableItems - maxAccountItems);

  if (!additionalItemsCount) {
    return null;
  }
  return (
    <ContactCardViewMoreButton
      additionalItemsCount={additionalItemsCount}
      onClick={() => setIsShowingAdditionalFields(true)}
    />
  );
}
